import { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  LinearProgress,
  Alert,
  CircularProgress,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DescriptionIcon from "@mui/icons-material/Description";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TonalityIcon from "@mui/icons-material/Tonality";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ScienceIcon from "@mui/icons-material/Science";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { LanguageContext } from "../../context/LanguageContext";
import { fetchContext } from "../../api/webchat";

export default function AgentCreateWizardDesktop({ open, onClose, tenantId }) {
  const { translate } = useContext(LanguageContext);
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [guidelines, setGuidelines] = useState("");
  const [tone, setTone] = useState("");
  const [saving, setSaving] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState("");
  const [contextInfo, setContextInfo] = useState("");
  const [lastFetchedUrl, setLastFetchedUrl] = useState("");

  const toneOptions = [
    { value: "friendly", labelKey: "ToneFriendly", Icon: SentimentSatisfiedAltIcon },
    { value: "professional", labelKey: "ToneProfessional", Icon: WorkOutlineIcon },
    { value: "concise", labelKey: "ToneConcise", Icon: BoltIcon },
    { value: "playful", labelKey: "TonePlayful", Icon: EmojiEmotionsIcon },
    { value: "supportive", labelKey: "ToneSupportive", Icon: VolunteerActivismIcon },
    { value: "empathetic", labelKey: "ToneEmpathetic", Icon: FavoriteBorderIcon },
    { value: "technical", labelKey: "ToneTechnical", Icon: ScienceIcon },
    { value: "neutral", labelKey: "ToneNeutral", Icon: TonalityIcon },
  ];

  const canNext = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return isValidUrl(website) && !contextLoading;
    if (step === 2) return guidelines.trim().length > 0;
    if (step === 3) return !!tone;
    return true;
  };

  const totalSteps = 4;
  const percent = Math.round(((step + 1) / totalSteps) * 100);

  const resetFields = () => {
    setStep(0);
    setName("");
    setWebsite("");
    setGuidelines("");
    setTone("");
    setContextLoading(false);
    setContextError("");
    setContextInfo("");
    setLastFetchedUrl("");
  };

  const handleClose = () => {
    if (saving) return;
    resetFields();
    onClose?.();
  };

  const handleCreate = async () => {
    if (!tenantId || saving) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "tenants", tenantId, "agents"), {
        name: name.trim(),
        website: website.trim(),
        description: "",
        guidelines: guidelines.trim(),
        tone,
        publicEnabled: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      onClose?.();
      resetFields();
    } finally {
      setSaving(false);
    }
  };

  const isValidUrl = (value) => {
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handlePrefillContext = async (targetUrl) => {
    if (!tenantId || !targetUrl || contextLoading) return;
    setContextLoading(true);
    setContextError("");
    setContextInfo("");
    try {
      const resp = await fetchContext(tenantId, targetUrl);
      const ctx = resp?.contextText || "";
      if (ctx) {
        setGuidelines(ctx);
        setContextInfo("Contexte pré-rempli depuis le site.");
      } else {
        setContextInfo("Aucun contenu détecté, vous pouvez remplir manuellement.");
      }
    } catch (e) {
      setContextError("Impossible de récupérer le contenu du site. Vérifiez l'URL ou réessayez.");
    } finally {
      setContextLoading(false);
    }
  };

  useEffect(() => {
    if (!website.trim()) {
      setContextError("");
      setContextInfo("");
      return;
    }
    if (!isValidUrl(website.trim())) {
      setContextError("URL invalide (utilisez http ou https).");
      setContextInfo("");
      return;
    }
    setContextError("");
    // Évite de relancer si déjà fetché la même URL
    if (website.trim() === lastFetchedUrl) return;
    setLastFetchedUrl(website.trim());
    handlePrefillContext(website.trim());
  }, [website, tenantId]);

  const Title = ({ icon: IconCmp, title, subtitle }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconCmp sx={{ color: "#00E676" }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </Box>
      {subtitle && (
        <Box sx={{
          mt: 1,
          p: 1.2,
          borderRadius: 1.5,
          bgcolor: "rgba(0,230,118,0.08)",
          border: "1px solid rgba(0,230,118,0.25)",
        }}>
          <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {translate("CreateAgent") || "Create your agent"}
      </DialogTitle>
      <Box sx={{ px: 3, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {step + 1}/{totalSteps}
          </Typography>
          <Typography variant="caption" color="text.secondary">{percent}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={percent} sx={{ height: 6, borderRadius: 3 }} />
      </Box>
      <DialogContent dividers sx={{ minHeight: 480, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {step === 0 && (
          <>
            <Title
              icon={SmartToyIcon}
              title={(translate && translate("AgentName")) || "Agent name"}
              subtitle={(translate && translate("AgentNameHelp")) || "Give your assistant a short, memorable name. You can change it later."}
            />
            <TextField fullWidth autoFocus label={(translate && translate("AgentName")) || "Agent name"} value={name} onChange={(e) => setName(e.target.value)} />
          </>
        )}

        {step === 1 && (
          <>
            <Title
              icon={DescriptionIcon}
              title="URL du site web"
              subtitle="Nous utiliserons cette URL pour récupérer les infos clés de votre site et pré-remplir l'agent."
            />
            <Stack spacing={2}>
              <TextField
                fullWidth
                autoFocus
                label="URL du site"
                placeholder="https://www.votresite.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                error={!!contextError}
                helperText={contextError || ""}
              />
              {contextLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Analyse en cours…</Typography>
                </Box>
              )}
              {contextInfo && !contextError && (
                <Alert severity="success">{contextInfo}</Alert>
              )}
            </Stack>
          </>
        )}

        {step === 2 && (
          <>
            <Title
              icon={PsychologyIcon}
              title={(translate && translate("AgentContext")) || "Context & persona"}
              subtitle={(translate && translate("AgentContextHelp")) || "Describe how your agent should speak, behave, and what it should focus on."}
            />
            <TextField fullWidth multiline minRows={6} label={(translate && translate("AgentContext")) || "Context & persona"} value={guidelines} onChange={(e) => setGuidelines(e.target.value)} />
          </>
        )}

        {step === 3 && (
          <>
            <Title
              icon={TonalityIcon}
              title={(translate && translate("AgentTone")) || "Tone"}
              subtitle={(translate && translate("AgentToneHelp")) || "Pick a tone that fits your brand. You can adjust later."}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {toneOptions.map((opt) => (
                <Button key={opt.value} variant={tone === opt.value ? "contained" : "outlined"} onClick={() => setTone(opt.value)} startIcon={<opt.Icon />} sx={{ textTransform: "none" }}>
                  {(translate && translate(opt.labelKey)) || opt.value}
                </Button>
              ))}
            </Stack>
          </>
        )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving} sx={{ mr: "auto" }}>
          {translate("Cancel") || "Cancel"}
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={saving} variant="outlined">
            {translate("Back") || "Back"}
          </Button>
        )}
        {step < totalSteps - 1 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext() || saving} variant="contained">
            {translate("Next") || "Next"}
          </Button>
        ) : (
          <Button onClick={handleCreate} disabled={!canNext() || saving} variant="contained">
            {translate("Create") || "Create"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

